import useDeviceId from '#/hooks/useDeviceId';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import { ROUTE_NAMES } from '#/router/routes';
import ApiManager from '#/services/api/ApiManager';
import { spotifyApiConfig } from '#/utils/constants';
import msToReadableTime from '#/utils/msToReadableTime';
import React, { HTMLAttributes, ImgHTMLAttributes, useState } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

interface IArtistAlbum {
  name: string;
  id: string;
}

interface IProperties {
  id: string;
  image?: ImgHTMLAttributes<HTMLImageElement>['src'];
  name: string;
  duration: number;
  explicit: boolean;
  number?: number;
  followed: boolean;
  artists?: IArtistAlbum[];
  album?: IArtistAlbum;
  dateAdded?: string;
  onPlay?: HTMLAttributes<HTMLDivElement>['onClick'];
  onFavorite?: HTMLAttributes<HTMLAnchorElement>['onClick'];
  uri?: string;
  contextUri?: string;
}

const TrackListItem = (properties: IProperties) => {
  const {
    id,
    number,
    image,
    name,
    duration,
    explicit,
    followed,
    artists,
    album,
    dateAdded,
    onPlay,
    onFavorite,
    uri,
    contextUri,
  } = properties;

  const protectedSpotify = useProtectedSpotify();

  const { retrieveDeviceId } = useDeviceId();

  const handleClickPlay = async () => {
    const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

    const response = await apiManager.replaceEntity(
      'playerPlay',
      '',
      {
        context_uri: contextUri,
        offset: { uri: uri },
      },
      {
        device_id: retrieveDeviceId(),
      }
    );
    if ('error' in response) {
      // TODO: handle error
      // eslint-disable-next-line no-console
      console.log('Cant reproduce');
    }
  };

  const [hover, setHover] = useState(false);

  const handleMouseEnter = () => setHover(true);

  const handleMouseLeave = () => setHover(false);

  const grayTextStyle = ' text-theme-light-gray text-sm';

  const PlayButton = ({
    onClick,
  }: {
    onClick: HTMLAttributes<HTMLSpanElement>['onClick'];
  }) => (
    <span onClick={onClick} className="material-icons cursor-default">
      play_arrow
    </span>
  );

  return (
    <div
      className="flex flex-row justify-between gap-4 items-center h-14 transition-colors duration-300 hover:bg-theme-white/10 p-2 rounded-md"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDoubleClick={onPlay}
    >
      {number && (
        <div className="flex justify-center items-center w-4 text-center h-full">
          {hover ? (
            <PlayButton onClick={handleClickPlay} />
          ) : (
            <span className="text-theme-light-gray">{number}</span>
          )}
        </div>
      )}
      <div className="flex-[3] flex gap-4 min-w-0 items-center">
        {image && (
          <div className="relative w-10 aspect-square overflow-hidden">
            <img className="h-full object-cover" src={image} alt="" />
            {number === undefined && hover && (
              <div className="h-full w-full flex justify-center items-center absolute top-0  bg-theme-black/40">
                <PlayButton onClick={handleClickPlay} />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col min-w-0 overflow-hidden whitespace-nowrap text-ellipsis">
          <Link
            to={`${ROUTE_NAMES.TRACK}/${id}`}
            className="text-theme-white hover:underline one-line-text"
          >
            {name}
          </Link>
          <div className="flex gap-2">
            {explicit && (
              <span className="material-icons text-theme-light-gray text-xl leading-none">
                explicit
              </span>
            )}
            {artists && (
              <span className={grayTextStyle}>
                {artists.map((artist, index) => (
                  <span key={artist.id}>
                    <Link
                      key={artist.id}
                      to={`${ROUTE_NAMES.ARTIST}/${artist.id}`}
                      className={`${
                        hover ? 'text-theme-white' : ''
                      } hover:underline cursor-pointer`}
                    >
                      {artist.name}
                    </Link>
                    {index === artists.length - 1 ? '' : ', '}
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>
      </div>

      {album && (
        <div className="flex-[2] flex flex-col min-w-0 overflow-hidden whitespace-nowrap text-ellipsis">
          <Link
            to={`${ROUTE_NAMES.ALBUM}/${album.id}`}
            className={twMerge(grayTextStyle, 'hover:underline one-line-text')}
          >
            {album.name}
          </Link>
        </div>
      )}

      {dateAdded && (
        <div className="flex-[2] flex flex-col min-w-0 overflow-hidden whitespace-nowrap text-ellipsis">
          <span className={grayTextStyle}>{dateAdded}</span>
        </div>
      )}

      <span
        onClick={onFavorite}
        className={`material-icons text-lg cursor-default ${
          followed
            ? 'text-theme-green'
            : hover
            ? 'text-theme-light-gray hover:text-white '
            : 'text-transparent'
        }  `}
      >
        {followed ? 'favorite' : 'favorite_border'}{' '}
      </span>

      <span className="ml-4 cursor-default text-sm text-theme-light-gray w-10">
        {msToReadableTime(duration)}
      </span>
    </div>
  );
};

export default TrackListItem;
